import React, { useEffect } from 'react';
import { HashRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import SaleBanner from './components/SaleBanner';
import DesktopToolSidebar from './components/DesktopToolSidebar';
import Login from './pages/Login';
import Signup from './pages/Signup';
import GoogleSignup from './pages/GoogleSignup';
import Terms from './pages/Terms';
import HomeWithStoryCTA from './pages/HomeWithStoryCTA';
import RecruitmentList from './pages/RecruitmentList';
import RecruitmentDetail from './pages/RecruitmentDetail';
import CreateRecruitment from './pages/CreateRecruitment';
import EditRecruitment from './pages/EditRecruitment';
import ApplicationHistory from './pages/ApplicationHistory';
import Profile from './pages/Profile';
import EditProfile from './pages/EditProfile';
import ChangeEmail from './pages/ChangeEmail';
import ChangePassword from './pages/ChangePassword';
import MountainLog from './pages/MountainLog';
import FareSplitPage from './pages/FareSplitPage';
import MountainTypePage from './pages/MountainTypePage';
import Achievements from './pages/Achievements';
import Contact from './pages/Contact';
import CommunityGuidelines from './pages/CommunityGuidelines';
import FirstTimeGuide from './pages/FirstTimeGuide';
import Faq from './pages/Faq';
import StorySubmission from './pages/StorySubmission';
import AdminKpiReport from './pages/AdminKpiReport';
import EmailDeliveryTest from './pages/EmailDeliveryTest';
import Chat from './pages/Chat';
import ChatList from './pages/ChatList';
import Notifications from './pages/Notifications';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import UserProfile from './pages/UserProfile';
import MyRecruitments from './pages/MyRecruitments';
import GroupChat from './pages/GroupChat';
import BoardHome from './pages/board/BoardHome';
import BoardCategory from './pages/board/BoardCategory';
import ThreadList from './pages/board/ThreadList';
import ThreadDetail from './pages/board/ThreadDetail';
import NewThread from './pages/board/NewThread';
import BoardSearch from './pages/board/BoardSearch';
import ProtectedRoute from './components/ProtectedRoute';
import BottomNav from './components/BottomNav';
import Footer from './components/Footer';
import RouteAnalytics from './components/RouteAnalytics';
import ToastHost from './components/ToastHost';
import NotificationDeliveryRegistration from './components/NotificationDeliveryRegistration';
import './App.css';

const APP_NAME = 'ヤマッチ';
const HOME_SEO_TITLE = 'ヤマッチ「登山仲間募集サイト」';

const INDEXABLE_PATHS = new Set([
  '/',
  '/recruitments',
  '/first-time-guide',
  '/faq',
  '/community-guidelines',
  '/contact',
]);

function getPrefectureSeoName(prefecture) {
  if (!prefecture) return '';
  if (prefecture === '北海道') return prefecture;
  return prefecture.replace(/[都府県]$/, '');
}

function upsertMeta(name, content) {
  let meta = document.head.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
}

function AppSeo() {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prefecture = params.get('prefecture') || '';
    const normalizedPrefecture = getPrefectureSeoName(prefecture);
    const isRecruitmentList = location.pathname === '/recruitments';

    if (location.pathname === '/') {
      document.title = HOME_SEO_TITLE;
      upsertMeta('description', 'ヤマッチは全国の登山仲間を探せる登山仲間募集サイトです。都道府県別に登山メンバー募集・同行者募集を確認できます。');
    } else if (isRecruitmentList && normalizedPrefecture) {
      document.title = `登山仲間募集　${normalizedPrefecture} | ${APP_NAME}`;
      upsertMeta('description', `${normalizedPrefecture}の登山仲間募集一覧です。日帰り登山から縦走まで、ヤマッチで同行者や登山メンバーを探せます。`);
    } else if (isRecruitmentList) {
      document.title = `登山仲間募集一覧 | ${APP_NAME}`;
      upsertMeta('description', '全国の登山仲間募集一覧です。都道府県や登山エリアから、同行者や登山メンバーを探せます。');
    } else {
      document.title = `${APP_NAME}`;
    }

    const robots = INDEXABLE_PATHS.has(location.pathname)
      ? 'index, follow'
      : 'noindex, nofollow';
    upsertMeta('robots', robots);
  }, [location.pathname, location.search]);

  return null;
}

function AppLayout() {
  const location = useLocation();
  const hideSidebar = [
    '/login',
    '/signup',
    '/reset-password',
    '/google-signup',
    '/verify-email',
    '/change-email',
    '/change-password',
    '/create-recruitment',
    '/profile/edit',
  ].some((prefix) => location.pathname.startsWith(prefix)) ||
    location.pathname.startsWith('/edit-recruitment/') ||
    location.pathname.startsWith('/chat/') ||
    location.pathname.startsWith('/group-chat/') ||
    location.pathname.startsWith('/email-delivery-test') ||
    location.pathname.startsWith('/ops-report');

  return (
    <div className="app">
      <AppSeo />
      <RouteAnalytics />
      <ToastHost />
      <NotificationDeliveryRegistration />
      <SaleBanner />
      <Navbar />
      <div className={`app-layout ${hideSidebar ? 'app-layout--full' : ''}`}>
        <main className="main-content">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/google-signup" element={<GoogleSignup />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/" element={<HomeWithStoryCTA />} />
            <Route path="/mountain-log" element={<MountainLog />} />
            <Route path="/fare-split" element={<FareSplitPage />} />
            <Route path="/mountain-type" element={<MountainTypePage />} />
            <Route path="/achievements" element={<Achievements />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/community-guidelines" element={<CommunityGuidelines />} />
            <Route path="/first-time-guide" element={<FirstTimeGuide />} />
            <Route path="/faq" element={<Faq />} />
            <Route
              path="/ops-report"
              element={
                <ProtectedRoute>
                  <AdminKpiReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/email-delivery-test"
              element={
                <ProtectedRoute>
                  <EmailDeliveryTest />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stories/submit"
              element={
                <ProtectedRoute>
                  <StorySubmission />
                </ProtectedRoute>
              }
            />
            <Route path="/recruitments" element={<RecruitmentList />} />
            <Route path="/recruitments/:id" element={<RecruitmentDetail />} />
            <Route
              path="/create-recruitment"
              element={
                <ProtectedRoute>
                  <CreateRecruitment />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-recruitment/:id"
              element={
                <ProtectedRoute>
                  <EditRecruitment />
                </ProtectedRoute>
              }
            />
            <Route
              path="/history"
              element={
                <ProtectedRoute>
                  <ApplicationHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/edit"
              element={
                <ProtectedRoute>
                  <EditProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-email"
              element={
                <ProtectedRoute>
                  <ChangeEmail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat/:chatId"
              element={
                <ProtectedRoute>
                  <Chat />
                </ProtectedRoute>
              }
            />
            <Route
              path="/group-chat/:recruitmentId"
              element={
                <ProtectedRoute>
                  <GroupChat />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chats"
              element={
                <ProtectedRoute>
                  <ChatList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-recruitments"
              element={
                <ProtectedRoute>
                  <MyRecruitments />
                </ProtectedRoute>
              }
            />
            <Route path="/users/:uid" element={<UserProfile />} />
            {/* 掲示板（Phase 2） */}
            <Route path="/board" element={<BoardHome />} />
            <Route path="/board/search" element={<BoardSearch />} />
            <Route path="/board/new-thread" element={<ProtectedRoute><NewThread /></ProtectedRoute>} />
            <Route path="/board/:categoryId" element={<BoardCategory />} />
            <Route path="/board/:categoryId/:subCategoryId" element={<ThreadList />} />
            <Route path="/board/:categoryId/:subCategoryId/:threadId" element={<ThreadDetail />} />
          </Routes>
        </main>
        {!hideSidebar && <DesktopToolSidebar />}
      </div>
      <Footer />
      <BottomNav />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppLayout />
      </Router>
    </AuthProvider>
  );
}

export default App;
